import React, { useState, useMemo } from 'react';
import styles from './index.module.css';
import { Dialog, DialogSurface, DialogBody } from '@fluentui/react-components';
import { Dismiss12Regular, Share20Regular } from '@fluentui/react-icons';
import { useSelector, useDispatch } from 'react-redux';
import { ShareNotepad, UnshareNotepad } from '../../redux/dashboardSlice';

const ShareSectionModal = ({ isOpen, onOpenChange, sectionId, setIsShareModalOpen }) => {
    const dispatch = useDispatch();
    const { projectUsersAll } = useSelector(state => state.team);
    const { user } = useSelector(state => state.user);
    const { notes } = useSelector(state => state.dashboard);

    // Get current notepad data to see who it's already shared with
    const currentNotepad = notes?.find(notepad => notepad.id === sectionId);

    // Debug logging to see all available properties

    // Try different possible property names for shared users
    const alreadySharedWith = Array.isArray(currentNotepad?.shared_with) ? currentNotepad.shared_with : [];

    // Ensure each project member appears only once (even if they belong to multiple groups)
    const uniqueProjectUsers = useMemo(() => {
        if (!Array.isArray(projectUsersAll)) return [];

        const seen = new Set();

        return projectUsersAll.filter(item => {
            const value = item?.value || {};
            const key =
                value.user_id ??
                (typeof value.email === 'string' && value.email.toLowerCase()) ??
                (typeof value.full_name === 'string' && value.full_name.toLowerCase());

            // If we can't form a stable key, keep the entry (very rare edge case)
            if (key === undefined || key === null || key === false) {
                return true;
            }

            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    }, [projectUsersAll]);

    const handleTeamMemberClick = async member => {
        // Instantly share the notepad with this user (additive)
        try {
            await dispatch(ShareNotepad({ notepadId: sectionId, userIds: [member.user_id] })).unwrap();
        } catch (error) {
            console.error('Failed to share notepad:', error);
        }
    };

    const handleRevoke = async userId => {
        if (!sectionId || !userId) return;

        try {
            await dispatch(UnshareNotepad({ notepadId: sectionId, userId })).unwrap();
        } catch (error) {
            console.error('Failed to revoke access:', error);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogSurface className={styles.shareModal}>
                <DialogBody className={styles.shareModalBody}>
                    <div>
                        <p className={styles.sectionShareModalTitle}>Share Section</p>
                        <p className={styles.sectionShareModalSubtitle}>Add Project Team Members</p>
                        <div>
                            {/* All team members - show Share or Revoke Access based on status */}
                            {uniqueProjectUsers
                                ?.filter(item => item?.value?.user_id !== user?.id)
                                .filter(item => !item?.value?.invitation)
                                .map(item => {
                                    const isAlreadyShared = alreadySharedWith.includes(item?.value?.user_id);

                                    return (
                                        <div className="d-flex align-items-center justify-content-between" key={item?.value?.user_id}>
                                            <p className={styles.shareTeamMemberName}>{item?.value?.full_name}</p>
                                            <span
                                                className={styles.shareTeamMemberName}
                                                onClick={() => {
                                                    if (isAlreadyShared) {
                                                        handleRevoke(item?.value?.user_id);
                                                    } else {
                                                        handleTeamMemberClick(item?.value);
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}>
                                                {isAlreadyShared ? 'Revoke Access' : 'Share'}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default ShareSectionModal;
